import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import AdminLayout from './AdminLayout';
import { Search, Briefcase, Eye, Users, Trash2, AlertCircle } from 'lucide-react';

export default function AdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [msg, setMsg] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('job_listings')
        .select('*, companies(name, verification_status)')
        .order('created_at', { ascending: false });
      setJobs(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const deactivate = async (jobId) => {
    try {
      await supabase.from('job_listings')
        .update({ is_active: false }).eq('id', jobId);
      setJobs(prev => prev.map(j =>
        j.id === jobId ? { ...j, is_active: false } : j));
      setMsg({ type: 'success', text: 'Job deactivated.' });
      setTimeout(() => setMsg(null), 3000);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  };

  const filtered = jobs.filter(j => {
    if (filter === 'active' && !j.is_active) return false;
    if (filter === 'inactive' && j.is_active) return false;
    if (search) {
      const q = search.toLowerCase();
      return j.title?.toLowerCase().includes(q) ||
        j.companies?.name?.toLowerCase().includes(q);
    }
    return true;
  });

  const TYPE_COLORS = {
    full_time: '#10B981', internship: '#4F8EF7',
    remote: '#7C3AED', part_time: '#F59E0B',
    hybrid: '#22D3EE', contract: '#EF4444',
  };

  return (
    <AdminLayout>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1E293B' }}>
          Job Listings
        </h1>
        <p style={{ color: '#94A3B8', marginTop: 4, fontSize: 14 }}>
          All jobs posted across the platform
        </p>
      </div>

      {msg && (
        <div style={{
          padding: '10px 14px', borderRadius: 10,
          marginBottom: 16, fontSize: 13, fontWeight: 600,
          background: msg.type === 'success' ? '#F0FDF4' : '#FFF1F2',
          color: msg.type === 'success' ? '#16A34A' : '#E11D48',
          border: `1px solid ${msg.type === 'success' ? '#BBF7D0' : '#FECDD3'}`,
        }}>{msg.text}</div>
      )}

      {/* Filters */}
      <div style={{
        display: 'flex', gap: 10, marginBottom: 20,
        flexWrap: 'wrap', alignItems: 'center',
      }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 240 }}>
          <Search size={14} style={{
            position: 'absolute', left: 10,
            top: '50%', transform: 'translateY(-50%)',
            color: '#94A3B8',
          }} />
          <input value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search jobs or companies..."
            style={{
              width: '100%', padding: '9px 12px 9px 30px',
              border: '1.5px solid #E2E8F0',
              borderRadius: 10, fontSize: 13,
              background: '#fff', outline: 'none',
            }} />
        </div>
        {['all', 'active', 'inactive'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={{
              padding: '8px 14px', borderRadius: 8,
              border: filter === f
                ? '1.5px solid #4F8EF7'
                : '1.5px solid #E2E8F0',
              background: filter === f ? '#EEF2FF' : '#fff',
              color: filter === f ? '#4F8EF7' : '#64748B',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>
            {f.charAt(0).toUpperCase() + f.slice(1)} (
            {f === 'all' ? jobs.length
              : f === 'active' ? jobs.filter(j => j.is_active).length
                : jobs.filter(j => !j.is_active).length})
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={{
        background: '#fff', borderRadius: 16,
        border: '1px solid #E2E8F0', overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{
            display: 'flex', justifyContent: 'center', padding: 60,
          }}>
            <div className="spinner" />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: 60, color: '#94A3B8',
          }}>
            <Briefcase size={40} style={{ opacity: 0.3, margin: '0 auto 12px', display: 'block' }} />
            No jobs found
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F8FAFF' }}>
                {['Job Title', 'Company', 'Type', 'Location', 'Applied', 'Views', 'Status', 'Action'].map(h => (
                  <th key={h} style={{
                    padding: '11px 16px', textAlign: 'left',
                    fontSize: 11, fontWeight: 700,
                    color: '#94A3B8', letterSpacing: 0.5,
                    borderBottom: '1px solid #F1F5F9',
                  }}>{h.toUpperCase()}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(j => (
                <tr key={j.id} style={{
                  borderBottom: '1px solid #F1F5F9',
                  transition: 'background 0.1s',
                }}
                  onMouseEnter={e =>
                    e.currentTarget.style.background = '#F8FAFF'}
                  onMouseLeave={e =>
                    e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{
                      fontWeight: 600, fontSize: 13,
                      color: '#1E293B', maxWidth: 200,
                      overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>{j.title}</div>
                    <div style={{ fontSize: 10, color: '#94A3B8' }}>
                      {j.category}
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ fontSize: 12, color: '#475569' }}>
                      {j.companies?.name || '—'}
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700,
                      padding: '3px 8px', borderRadius: 5,
                      background: `${TYPE_COLORS[j.job_type] || '#94A3B8'}15`,
                      color: TYPE_COLORS[j.job_type] || '#94A3B8',
                    }}>
                      {j.job_type?.replace('_', ' ')?.toUpperCase()}
                    </span>
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    fontSize: 12, color: '#64748B',
                  }}>
                    {j.location_city}, {j.location_country}
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    fontSize: 13, color: '#1E293B', fontWeight: 600,
                    textAlign: 'center',
                  }}>
                    {j.applications_count || 0}
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    fontSize: 13, color: '#64748B',
                    textAlign: 'center',
                  }}>
                    {j.views_count || 0}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700,
                      padding: '3px 8px', borderRadius: 5,
                      background: j.is_active ? '#D1FAE5' : '#F1F5F9',
                      color: j.is_active ? '#10B981' : '#94A3B8',
                    }}>
                      {j.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {j.is_active && (
                      <button
                        onClick={() => deactivate(j.id)}
                        title="Deactivate job"
                        style={{
                          background: 'rgba(239,68,68,0.1)',
                          border: '1px solid rgba(239,68,68,0.25)',
                          borderRadius: 6, padding: '5px 8px',
                          cursor: 'pointer', color: '#EF4444',
                          display: 'flex', alignItems: 'center', gap: 4,
                          fontSize: 11, fontWeight: 600,
                        }}>
                        <Trash2 size={12} />Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}